// src/pages/BlogPage.jsx

import React, { useState, useCallback, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import BlogHero from '../components/blog/BlogHero';
import BlogGrid from '../components/blog/BlogGrid';
import BlogSidebar from '../components/blog/BlogSidebar';
import Pagination from '../components/blog/Pagination';
import NewsletterSignup from '../components/blog/NewsletterSignup';
import { useBlogs } from '../hooks/useBlogs';

const BlogPage = () => {
  const { isDark } = useTheme();
  const [imageErrors, setImageErrors] = useState({});
  
  const {
    blogs,
    tagsList,
    recentPosts,
    categoriesList,
    activeCategory,
    setActiveCategory,
    activeTag,
    setActiveTag,
    currentPage,
    totalPages,
    changePage,
    searchQuery,
    setSearchQuery,
    searchResults,
    searching,
    clearSearch,
    loading,
    error,
    getDisplayedPosts,
    clearFilters
  } = useBlogs();

  useEffect(() => {
    console.log("BlogPage mounted");

    return () => {
      console.log("BlogPage unmounted");
    };
  }, []);

  const displayedPosts = React.useMemo(() => {
    if (loading || error) return [];
    return getDisplayedPosts();
  }, [loading, error, getDisplayedPosts]);

  const featuredPost = React.useMemo(() => {
    return displayedPosts.find(post => post?.is_featured);
  }, [displayedPosts]);

  const regularPosts = React.useMemo(() => {
    if (!featuredPost) return displayedPosts;
    return displayedPosts.filter(post => post?.id !== featuredPost.id);
  }, [displayedPosts, featuredPost]);

  const handleImageError = useCallback((imagePath) => {
    setImageErrors(prev => ({ ...prev, [imagePath]: true }));
  }, []);

  const handleCategoryChange = useCallback((categorySlug) => {
    setActiveCategory(categorySlug);
    clearSearch();
  }, [clearSearch, setActiveCategory]);

  const handleTagClick = useCallback((tagSlug) => {
    setActiveTag(activeTag === tagSlug ? null : tagSlug);
    setSearchQuery('');
    setActiveCategory('all');
  }, [activeTag, setActiveTag, setSearchQuery, setActiveCategory]);

  // Loading state
  if (loading) {
    return (
      <div className={`pt-20 min-h-screen flex items-center justify-center ${
        isDark ? 'bg-black' : 'bg-gray-50'
      }`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-700 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            Loading articles...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`pt-20 min-h-screen flex items-center justify-center ${
        isDark ? 'bg-black' : 'bg-gray-50'
      }`}>
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">⚠️</div>

          <h2 className={`text-2xl font-bold mb-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            Something went wrong
          </h2>

          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
            {error}
          </p>

          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-700 to-blue-900 text-white font-semibold hover:scale-105 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">

      {/* Hero Section */}
      <BlogHero isDark={isDark} />

      {/* Featured Post Section */}
      {featuredPost && !searchResults && (
        <section className={`py-12 ${
          isDark ? 'bg-black' : 'bg-white'
        }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            <div className="mb-8">
              <h2 className={`text-2xl font-bold mb-2 ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>
                Featured Article
              </h2>

              <p className={
                isDark ? 'text-gray-400' : 'text-gray-600'
              }>
                Our editors' pick of the week
              </p>
            </div>

            <BlogGrid 
              blogs={[featuredPost]} 
              variant="featured"
              isDark={isDark}
              onImageError={handleImageError}
            />
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className={`py-12 ${
        isDark ? 'bg-gray-900/30' : 'bg-blue-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

            {/* Sidebar */}
            <BlogSidebar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchResultCount={searchResults?.length || null}
              categories={categoriesList}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange}
              recentPosts={recentPosts}
              tags={tagsList}
              activeTag={activeTag}
              onTagClick={handleTagClick}
              isDark={isDark}
              onImageError={handleImageError}
            />

            {/* Blog Posts Grid */}
            <div className="lg:col-span-3 order-1 lg:order-2">

              <BlogGrid 
                blogs={displayedPosts}
                variant="grid"
                isDark={isDark}
                onImageError={handleImageError}
                emptyMessage={
                  searchQuery 
                    ? `No results found for "${searchQuery}"`
                    : activeCategory !== 'all'
                    ? `No posts found in "${categoriesList.find(c => c.slug === activeCategory)?.name || activeCategory}" category`
                    : activeTag
                    ? `No posts found with tag "${tagsList.find(t => t.slug === activeTag)?.name || activeTag}"`
                    : "No posts found"
                }
              />

              {!searchQuery && totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={changePage}
                  isDark={isDark}
                />
              )}

              {/* Search result indicator */}
              {searchQuery && searchResults && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                      setActiveTag(null);
                    }}
                    className={`text-sm ${
                      isDark
                        ? 'text-blue-500'
                        : 'text-blue-700'
                    } hover:underline`}
                  >
                    ← Clear search and show all posts
                  </button>
                </div>
              )}

              {/* Active filters indicator */}
              {(activeCategory !== 'all' || activeTag) && !searchQuery && (
                <div className="mt-6 text-center">
                  <button
                    onClick={clearFilters}
                    className={`text-sm ${
                      isDark
                        ? 'text-gray-400'
                        : 'text-gray-600'
                    } hover:underline`}
                  >
                    ← Clear all filters
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className={`py-16 ${
        isDark ? 'bg-black' : 'bg-white'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <NewsletterSignup isDark={isDark} />
        </div>
      </section>

    </div>
  );
};

export default BlogPage;